import * as React from 'react';
import {DataResponse, ErrorCategory, StandardResponseData} from '../models/DataResponse';
import DataService from '../services/DataService';
import ConfirmationDialog, {
  ConfirmationDialogOptions,
  ConfirmationType
} from '../components/ConfirmationDialog/ConfirmationDialog';
import SingletonDialogService from '../services/SingletonDialogService';
import UserService from '../services/UserService';

export function confirmServerAction<T = StandardResponseData>(
  dialogOptions: ConfirmationDialogOptions,
  url: string,
  requestBody: any): Promise<DataResponse<T> | undefined> {

  return new Promise<DataResponse<T> | undefined>(
    async (resolve: (result: DataResponse<T> | undefined) => void,
           reject: (reason?: any) => void) => {
      let okPressed = await ConfirmationDialog.show(dialogOptions);

      if (okPressed) {
        try {
          let result = await DataService.fetch<T>(url, requestBody);
          resolve(result);
        } catch (ex) {
          showErrorIfRequestFailed(ex);
          resolve(ex);
        }
      } else {
        reject();
      }
    });
}

export function showErrorIfRequestFailed(response: DataResponse<any>) {
  if (response.statusCode < 400) {
    return;
  }

  showErrorMessage('code').then();
}

export async function showErrorMessage(category: ErrorCategory = 'network') {
  const {title, content} = getErrorMessageContent(category);

  if (SingletonDialogService.isDialogInGroupOpen('ERROR_MESSAGE')) {
    return;
  }

  ConfirmationDialog.show({
    type: ConfirmationType.Exclamation,
    title: title,
    maxWidth: 400,
    showCancel: false,
    okText: 'Close',
    okIcon: 'cross-circle-o',
    singletonDialogGroupCode: 'ERROR_MESSAGE',
    content: content,
    footerTraceMessage: 'TRACE: ' + UserService.instance.correlationId
  });
}

function getErrorMessageContent(category: ErrorCategory):
  { title: string; content: React.ReactNode } {
  const supportLink = <a href={'mailto:support@mindsetmanage.com'}>{'support@mindsetmanage.com'}</a>;
  switch (category) {
    case 'code':
      return {
        title: 'Something went wrong',
        content: (
          <div>
            <span>
              {'An unexpected error occurred. Please refresh or reload ' +
                'your browser and try again. If the issue persists, please contact '}
            </span>
            {supportLink}
          </div>
        )
      };
    case 'network':
    default:
      return {
        title: 'Unable to access the system',
        content: (
          <div>
            <span>
              {'You appear to be offline or experiencing a connection issue. ' +
                'Please check your internet connection, then refresh or reload ' +
                'your browser. If the issue persists, please contact '}
            </span>
            {supportLink}
          </div>
        )
      };
  }
}

export async function showCustomErrorMessage(title: string, content: string | React.ReactNode) {
  await ConfirmationDialog.show({
    type: ConfirmationType.Exclamation,
    title: title,
    content: content,
    okText: 'Close',
    okIcon: 'cross-circle-o',
    showCancel: false
  });
}

export async function showCustomErrorMessageAdvanced(
  title: string,
  content: string | React.ReactNode,
  onOkayText: string | React.ReactNode,
  onOkay: () => void,
  cancelText: string | React.ReactNode) {

  let okPressed = await ConfirmationDialog.show({
    type: ConfirmationType.Exclamation,
    title: title,
    content: content,
    okText: onOkayText,
    okIcon: <span/>,
    showCancel: true,
    cancelText: cancelText
  });

  if (okPressed) {
    onOkay();
  }
}

export async function showSuccessMessage(title: string, content: string | React.ReactNode) {
  await ConfirmationDialog.show({
    type: ConfirmationType.Success,
    title: title,
    content: content,
    okText: 'Close',
    okIcon: 'check-circle-o',
    showCancel: false
  });
}

export async function confirmAbandonChanges(onChangesAbandoned: () => void) {
  let okPressed = await ConfirmationDialog.show({
    title: 'Abandon changes',
    content: 'You are about to lose your changes. Continue?'
  });

  if (okPressed) {
    onChangesAbandoned();
  }
}
